import React from 'react'
import styled from 'styled-components'
import FormattingUtils from 'shared/FormattingUtils'
import { NumberFormatter, NumberInput } from 'core-system/Input/NumberInput'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { SpaceProps } from 'styled-system'

const StaticContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: ${(props) => props.theme.palette.primary.pink300};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
`

interface BudgetUsesSelectorProps extends SpaceProps {
  budget: number
  recBudget: number
  recUses: number
  uses: number
  onBudgetChange: (budget: number) => void
  onUsesChange: (uses: number) => void
  budgetIncrementVal?: number
  budgetError?: boolean
  usesError?: boolean
  canEdit?: boolean
  budgetSubtitle?: string
  showUses?: boolean
}

const BudgetUsesSelector = (props: BudgetUsesSelectorProps) => {
  const {
    budget,
    onBudgetChange,
    budgetIncrementVal = 5,
    budgetError,
    uses,
    onUsesChange,
    canEdit = true,
    usesError,
    budgetSubtitle = 'Max Transaction Limit',
    showUses = true,
  } = props

  return (
    <>
      <Text variant='h5' marginBottom='1rem'>
        How Much?
      </Text>
      <>
        <Text
          variant='body2'
          textColor={palette.text.secondary}
          marginBottom='0.5rem'
        >
          {budgetSubtitle}
        </Text>
        {canEdit ? (
          <NumberInput
            padding='1rem'
            onValueChange={onBudgetChange}
            value={budget / 100}
            iconSize={20}
            incrementVal={budgetIncrementVal}
            maxHeight='2.5rem'
          >
            <NumberFormatter decimalScale={0} fontVariant='h5' />
          </NumberInput>
        ) : (
          <StaticContainer>
            <Text variant='h5'>{FormattingUtils.formatDollar(budget, 0)}</Text>
          </StaticContainer>
        )}
        {budgetError && (
          <Text
            variant='body1'
            textColor={palette.error.red500}
            marginTop='0.25rem'
          >
            Please enter a valid amount
          </Text>
        )}
      </>
      {showUses && (
        <>
          <Text variant='h5' marginBottom='1rem' marginTop='1.5rem'>
            How Often?
          </Text>
          <Text
            variant='body2'
            textColor={palette.text.secondary}
            marginBottom='0.5rem'
          >
            Annual Number of Transactions
          </Text>
          {canEdit ? (
            <NumberInput
              padding='1rem'
              onValueChange={onUsesChange}
              value={uses}
              iconSize={20}
              incrementVal={1}
              maxHeight='2.5rem'
            >
              <NumberFormatter
                decimalScale={0}
                fontVariant='h5'
                currencyFormat='count'
                placeholder={'0'}
              />
            </NumberInput>
          ) : (
            <StaticContainer>
              <Text variant='h1'>{uses}</Text>
            </StaticContainer>
          )}
          {usesError && (
            <Text
              variant='body1'
              textColor={palette.error.red500}
              marginTop='0.25rem'
            >
              Please enter a valid number
            </Text>
          )}
        </>
      )}
    </>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  BudgetUsesSelector.displayName = 'BudgetUsesSelector'
}

export default BudgetUsesSelector
